import { Component, OnInit, Inject, ViewChild, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { RemarkService } from '../../../services/remark.service';
import { PermalinkService } from '../../../services/permalink.service';
import { FileUploaderComponent } from '../../file-uploader/file-uploader.component';
import { finalize, take } from 'rxjs/operators';
import { animate, style, transition, trigger } from '@angular/animations';

import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'tt-send-remark-dialog',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatDialogModule,
    TranslateModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    FileUploaderComponent
],
  templateUrl: './send-map-remark-dialog.component.html',
  //styleUrls: ['./send-map-remark-dialog.component.scss']
  // '../send-map-remark-panel/send-map-remark-panel.component.scss',
  styleUrls: [
    './send-map-remark-dialog.component.scss',
    '../send-map-remark-panel/send-map-remark-panel.component.scss',
  ],
  animations: [
    trigger('fade', [
      transition('void => *', [
        style({ opacity: 0 }),
        animate(1000, style({ opacity: 1 }))
      ])
    ])
  ],
  standalone: true
})
export class SendMapRemarkDialogComponent implements OnInit {

  @ViewChild('fileUploader')
  private fileUploader: FileUploaderComponent;

  private formGroup: UntypedFormGroup;
  //private subscriptions: Subscription = new Subscription();
  //private messageType: string = 'remark'; // remark or email

  public loading: boolean;
  public fileUploaderEnabled: boolean = true;
  public msgIsSent: boolean;
  public errorIsSent: boolean;
  public error: string;
  public formdata: boolean;
  public allowedMIMETypes: Array<string> = ["image/png", "image/jpeg", "image/jpg"];
  public allowedExtensions: Array<string> = ["png", "jpeg", "jpg"];
  public uploadProgress: number;
  public uploadStatus: string = "preparing";
  public progressbarMode: string = "determinate";
  private recaptchaToken: string = null;
  public data = inject<any>(MAT_DIALOG_DATA);

  constructor(
    private translateService: TranslateService,
    private remarkService: RemarkService,
    private formBuilder: UntypedFormBuilder,
    private permalinkService: PermalinkService,
    public dialogRef: MatDialogRef<SendMapRemarkDialogComponent>,
   // @Inject(MAT_DIALOG_DATA) public data
  ) { }

  ngOnInit(): void {
    //on ios requesting the recaptcha token via iframe
    //to avoid unauthorized errors
    if (Capacitor.isNativePlatform()) {
      window.addEventListener('message', (event) => {
        if (event.data?.recaptchaToken) {
          this.recaptchaToken = event.data.recaptchaToken;
          //console.log('Received reCAPTCHA token:', this.recaptchaToken);
        }
        //refreshes for new token:
        //document.getElementById('recaptcha-frame')?.setAttribute('src', 'assets/recaptcha-proxy.html');
      });
      //execute captcha request 
      const iframe = document.getElementById('recaptcha-frame') as HTMLIFrameElement;
      iframe?.contentWindow?.postMessage('run-recaptcha', '*');
    }
    this.createForm();
    this.loading = false;
    this.uploadProgress = 0;
    this.msgIsSent = false;
    if (this.data.validateOnOpen) this.formGroup.markAllAsTouched();
    this.dialogRef.beforeClosed().pipe(take(1)).subscribe(() => {
      this.dialogRef.close([this.formGroup.value, this.formGroup.touched])
    });
  }

  /**
   * Builds a form and assigns validators to controls 
   */
  createForm() {
    let emailregex: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    this.formGroup = this.formBuilder.group({
      'firstName': [this.data.firstName, [Validators.maxLength(40)]],
      'lastName': [this.data.lastName, [Validators.maxLength(40)]],
      'email': [this.data.email, [Validators.required, Validators.pattern(emailregex)]],
      'message': [this.data.message, [Validators.required, Validators.maxLength(2000)]],//, Validators.minLength(5),
      'keepInformed': [this.data.keepInformed],
    });
  }

  /**
   * First name getter
   */
  get firstName() {
    return this.formGroup.get('firstName') as UntypedFormControl
  }

  /**
   * Last name getter
   */
  get lastName() {
    return this.formGroup.get('lastName') as UntypedFormControl
  }

  /**
   * Returns the correct validation error to the end user (email)
   */
  getErrorEmail() {
    return this.formGroup.get('email').hasError('required') ? 'general.validation.required' :
      this.formGroup.get('email').hasError('pattern') ? 'general.validation.email' : ''
  }

  /**
   * Returns the correct validation error to the end user (remark content)
   */
  getErrorMessage() {
    return this.formGroup.get('message').hasError('required') ? 'general.validation.required' :
      this.formGroup.get('message').hasError('maxlength') ? 'general.validation.maxlength' : ''
  }

  enableUploader() {
    this.fileUploaderEnabled = true;
  }

  /**
   * Builds an url from x,y & z
   * @param poi 
   * @returns current url
   */
  private getMarkerUrl(poi: any): string {
    let url = new URL(window.location.href)
    if (url.protocol === "capacitor:" && url.hostname === "localhost") {
      url.protocol = "https:";
      url.hostname = "topomapviewer.ngi.be";
    }
    let searchParams = new URLSearchParams(url.searchParams)
    //filter only nescessary search params
    url.searchParams.forEach((value, key) => {
      if (!['x', 'y', 'baselayer', 'zoom', 'l'].includes(key)) {
        searchParams.delete(key)
      }
    });
    searchParams.set('xm', poi.xm);
    searchParams.set('ym', poi.ym);
    searchParams.set('zm', poi.zm);
    searchParams.set('marker', 'cw.remark.point');
    return url.protocol + '//' + url.host + url.pathname + '?' + searchParams;
  }


  /**
   * Prepares payload, sends data to the server and handles the response
   * ? Split this method ? 
   * @param form 
   */
  public onSubmit(form) {
    this.loading = true;
    let data = form;
    data.languageCode = this.translateService.currentLang.toLowerCase();
    let poi = {
      xm: this.permalinkService.getUrlParam("x"),
      ym: this.permalinkService.getUrlParam("y"),
      zm: this.permalinkService.getUrlParam("zoom")
    }
    data.url = this.getMarkerUrl(poi);
    data.via = 'Topomapviewer';
    data.picturesStatus = this.fileUploader && this.fileUploader.getFiles().length > 0;

    let renamedFilesToUpload: File[] = new Array();
    if (this.fileUploader && this.fileUploader.getFiles().length > 0) {
      this.fileUploader.getFiles().forEach((notRenamedFile: File, index: number) => {
        // renaming the file 
        const renamedFile = new File([notRenamedFile], 'file_' + index + '.' + notRenamedFile.name.split('.').pop(), { type: notRenamedFile.type });
        renamedFilesToUpload.push(renamedFile);
      });

      this.remarkService.photoUploadProgress()
        .subscribe(
          (response) => {
            // parsing json response
            let data = JSON.parse(response['data'])
            let total: number = data['totalPictures']
            let uploaded: number = data['uploadedPictures']

            // calc of the progress 
            this.uploadProgress = uploaded * 100 / total;
            this.uploadStatus = data['uploadStatus']

            // changing the progress bar mode
            if (this.uploadStatus == "UPLOADING") {
              this.progressbarMode = "indeterminate"
            }

          },
          (error) => { console.log("SSE error", error) }
        )
    }
    // this.subscriptions.add(
    this.remarkService.sendCartowebRemark(data, renamedFilesToUpload, this.recaptchaToken)
      .pipe(
        take(1),
        finalize(() => {
          this.loading = false;
          if (renamedFilesToUpload.length > 0) {
            this.fileUploader.clearFileList();
            renamedFilesToUpload = [];
          }
        })
      )
      .subscribe({
        next: (response) => {
          this.msgIsSent = true;
          this.formGroup.markAsPristine();
          this.formGroup.markAsUntouched();
          this.data.bottomsheet.dismiss();
        },
        error: (error) => {
          this.errorIsSent = true;
          this.error = this.translateService.instant(
            'general.http-errors.' + error
          );
        }
      });

    // );
  }
}
